import React, { useEffect, useState } from "react";
import throttle from "lodash.throttle";
import { CalculatorTemplate } from "../../../../templates/CalculatorTemplate";
import { ICalculatorTabInformation } from "../../../../types/ICalculatorTabInformation";
import { IContentfulRichText } from "../../../../types/IContentfulRichText";
import {
  calculateMonthlyRepaymentAmount,
  convertCurrencyStringToFloat,
} from "../calculatorMethods";
import { formatNumberWithCommas } from "../stringFormatMethods";
import { RemortgageForm } from "./forms/remortgageForm";

export interface IProps {
  title?: string;
  description?: IContentfulRichText;
  disclaimer?: string;
  ctaText?: string;
  ctaUrl?: string;
  secondCtaText?: string;
  secondCtaUrl?: string;
  tabInformation?: ICalculatorTabInformation[];
  footerHeading?: string;
  footerText?: string;
  defaultTab: number;
  mortgageRateOverride: string;
  showExampleSavings?: boolean;
  isAtTopOfPage?: boolean;
  id: string;
  mortgageRateBasedOnLtv?: {defaultValue: number, rates: { ltv: number, mortgageRate: number }[]};
}

const DEBOUNCE_RESULTS = 1000;

const updateSaveAmount = async (
  setYearlySaveAmount: (amount: number) => void,
  monthlySaveAmount: number,
) => {
  if (!Number.isNaN(monthlySaveAmount)
    && monthlySaveAmount !== undefined
    && monthlySaveAmount !== Infinity
    && monthlySaveAmount !== -Infinity) {
    setYearlySaveAmount(monthlySaveAmount * 12);
  }
};

const throttleSaveAmount = throttle(updateSaveAmount, DEBOUNCE_RESULTS, {
  leading: false,
  trailing: true,
});

export const RemortgageCalculatorPanel: React.FunctionComponent<IProps> = (props) => {
  const {
    title,
    description,
    disclaimer,
    tabInformation,
    ctaText,
    ctaUrl,
    secondCtaText,
    secondCtaUrl,
    footerHeading,
    footerText,
    defaultTab,
    showExampleSavings,
    isAtTopOfPage,
    id,
    mortgageRateBasedOnLtv,
  } = props;

  const [propertyValue, setPropertyValue] = useState("");
  const [currentMonthlyRepayment, setCurrentMonthlyRepayment] = useState("");
  const [mortgageTerm, setMortgageTerm] = useState("");
  const [outstandingBorrowAmount, setOutstandingBorrowAmount] = useState("");

  const propertyValueF = convertCurrencyStringToFloat(propertyValue);
  const outstandingBorrowAmountF = convertCurrencyStringToFloat(
    outstandingBorrowAmount,
  );
  const LTV = outstandingBorrowAmountF > 0
    ? outstandingBorrowAmountF / propertyValueF
    : 0;

  const getMortgageRate = () => {
    if (mortgageRateBasedOnLtv) {
      const index = Math.floor(LTV * 10) / 10;
      let maxPossibleLTV = 0;

      const mortgageRateBasedOnLtvMap: {[key: string]: number} = mortgageRateBasedOnLtv.rates.reduce((total, { ltv, mortgageRate }) => {
        maxPossibleLTV = Math.max(maxPossibleLTV, ltv);
        return {
          ...total,
          [ltv]: mortgageRate,
        };
      }, {});

      if (LTV >= maxPossibleLTV) {
        return mortgageRateBasedOnLtvMap[maxPossibleLTV];
      }

      return mortgageRateBasedOnLtvMap[index] ?? mortgageRateBasedOnLtv.defaultValue;
    }

    return 6.34;
  };

  const monthlyInterestRate = getMortgageRate() / 100 / 12;
  const numberOfMonths = parseInt(mortgageTerm || "0", 10) * 12;
  const borrowAmount = parseFloat(
    (outstandingBorrowAmount && outstandingBorrowAmount.replace(/,/g, ""))
      || "0",
  );
  // F for float
  const currentMonthlyRepaymentF = parseFloat(
    (currentMonthlyRepayment && currentMonthlyRepayment.replace(/,/g, ""))
      || "0",
  );

  const newMonthlyRepaymentF = Math.round(
    calculateMonthlyRepaymentAmount(
      borrowAmount,
      monthlyInterestRate,
      numberOfMonths,
    ),
  );

  const monthlySaveAmountF = currentMonthlyRepaymentF - newMonthlyRepaymentF;
  const [oneYearSaveAmountF, setOneYearSaveAmountF] = useState<number | undefined>();

  useEffect(() => {
    throttleSaveAmount(
      setOneYearSaveAmountF,
      monthlySaveAmountF,
    );
  }, [monthlySaveAmountF]);

  const isSavingsPositive = oneYearSaveAmountF !== undefined && oneYearSaveAmountF > 0;
  const oneYearSaveAmountDisplay = isSavingsPositive
    ? `£${formatNumberWithCommas(Math.round(oneYearSaveAmountF).toString())}`
    : "£000";

  const subTitle = !outstandingBorrowAmount
    || !currentMonthlyRepayment
    || !mortgageTerm
    || isSavingsPositive ? (
      <>per year on your mortgage</>
    ) : (
      ""
    );
  const topTitle = !mortgageTerm
    || !outstandingBorrowAmount
    || !currentMonthlyRepayment
    || isSavingsPositive
    || oneYearSaveAmountF === undefined ? (
      "You could save up to"
    ) : (
      <span className="calculator-results__heading__negative-outcome">
        It looks like you have a good mortgage deal
        <br />
        and don’t need to remortgage
      </span>
    );

  const bottomTitle = (
    <>
      <p data-testid="mortgageRate">
        This is based on a new 2-year fixed or tracker rate mortgage with an interest rate
        of
        {" "}
        {getMortgageRate()}
        %.
      </p>
      <p>
        Your savings will vary depending on your circumstances.
        See below for how we calculate your potential savings.
      </p>
    </>
  );

  const exampleSavingsOptions = [
    {
      cost: 20,
      costSuffix: "hours",
      criteria: "I’m a parent with young kids",
      id: "parent",
      isCurrency: false,
      text: "of online tutoring for your kids",
    },
    {
      cost: 25,
      costSuffix: "hours",
      criteria: "I don't have children",
      id: "noChildren",
      isCurrency: false,
      text: "of remote personal training",
    },
    {
      cost: 25,
      costSuffix: "square feet",
      criteria: "I live in a rural area",
      id: "ruralArea",
      isCurrency: false,
      text: "of summer house/office garden",
    },
    {
      cost: 100,
      costSuffix: "nights",
      criteria: "I live in a city",
      id: "city",
      isCurrency: false,
      text: "in a lovely cottage in the Peak District",
    },
    {
      cost: 1.1,
      costSuffix: "square feet",
      criteria: "I have a garden",
      id: "garden",
      isCurrency: false,
      text: "of premium hardwood decking",
    },
    {
      cost: 10,
      costSuffix: "hours",
      criteria: "I wish I had a garden",
      id: "noGarden",
      isCurrency: false,
      text: "of Zipcar rental to drive to the country",
    },
    {
      cost: 1,
      costSuffix: "invested",
      criteria: "I love to travel",
      id: "travel",
      isCurrency: true,
      text: "in an ISA for next year's travel plans",
    },
    {
      cost: 75,
      costSuffix: "months",
      criteria: "I’m retired",
      id: "retired",
      isCurrency: false,
      text: "of golf club membership",
    },
  ];
  return (
    <>
      <CalculatorTemplate
        title={title || "Remortgage calculator"}
        description={
        description
        // eslint-disable-next-line max-len
        || "Use our remortgage calculator to work out how much you could save on your mortgage repayments by switching to a new mortgage deal. These remortgage savings calculations are for guidance only. You may have to pay an early repayment charge to your existing lender if you remortgage."
      }
        showExampleSavings={showExampleSavings}
        exampleSavingsOptions={exampleSavingsOptions}
        disclaimer={disclaimer || "*"}
        defaultTab={defaultTab}
        isAtTopOfPage={isAtTopOfPage}
        id={id}
        tabInformation={
        tabInformation || [
          {
            form: (
              <RemortgageForm
                propertyValue={propertyValue}
                currentMonthlyRepayment={currentMonthlyRepayment}
                mortgageTerm={mortgageTerm}
                outstandingBorrowAmount={outstandingBorrowAmount}
                setPropertyValue={setPropertyValue}
                setCurrentMonthlyRepayment={setCurrentMonthlyRepayment}
                setMortgageTerm={setMortgageTerm}
                setOutstandingBorrowAmount={setOutstandingBorrowAmount}
              />
            ),
            calculatorResults: {
              bottomTitle,
              bottom: {},
              cta: {
                title: ctaText || "Remortgage now",
                url: ctaUrl || "#",
              },
              secondCta: {
                title: secondCtaText,
                url: secondCtaUrl,
              },
              footer: {
                heading: footerHeading || "Your next steps:",
                text:
                  footerText
                  || "Our mortgage advisors will help you find the right mortgage for your cirmumstances.",
              },
              middle: {},
              top: {
                result:
                  outstandingBorrowAmount
                  && currentMonthlyRepayment
                  && mortgageTerm
                  && oneYearSaveAmountF
                  && oneYearSaveAmountF <= 0
                    ? ""
                    : oneYearSaveAmountDisplay,
                title: topTitle,
                subTitle,
              },
            },
            title: "What could you save?",
          },
        ]
      }
      />
    </>
  );
};
